import { IController, IComponentOptions, IScope, copy, IOnChangesObject } from "angular";
import { Unsubscribe } from "redux";
import colors from "wwwroot/json/colors.json";
import { IGoal } from "@gtmhub/goals/models";
import { ICheckin, IMetric } from "@gtmhub/okrs/metrics";
import { UpdateService, SupportedUpdateItem } from "@gtmhub/shared/services/updateService";
import { ITask } from "@gtmhub/tasks";
import { ITeam } from "@gtmhub/teams";
import { IUser } from "@gtmhub/users/models";
import { INgRedux } from "@gtmhub/state-management";
import {
  ItemEvents,
  CustomFieldTargetType,
  ICustomField,
  CustomFieldActions,
  ICustomFieldsStoreState,
  DropdownCustomFieldPredefinedValue,
  CustomFieldPredefinedValue,
} from "@gtmhub/customFields";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";

type CfDropdownUI = { color: string; value: string };
type CfMultiSelectUI = { value: string }[];
type CfSelectFromListUI = string[];

export const filterPredefinedValues = (query: string, predefinedValues: string[]): string[] => {
  return query ? predefinedValues.filter((value) => value.includes(query)) : predefinedValues;
};

interface CfMapUI {
  [cfName: string]: string | CfDropdownUI | CfMultiSelectUI | CfSelectFromListUI;
}

interface CfMapRequired {
  [cfName: string]: {
    value: string | CfDropdownUI | CfMultiSelectUI | CfSelectFromListUI;
    empty: boolean;
  };
}

export type CfMap = Record<string, string | string[] | unknown>;

interface IOnUpdateParams {
  customFields: CfMap;
}

export class EditableCustomFieldsCtrl implements IController {
  private itemEvents: ItemEvents;
  private unsubscribeFromCustomFields: () => void;
  private unsubscribeFromStore: Unsubscribe;

  // bindings
  target: IGoal | IMetric | ITask | ITeam | IUser | ICheckin;
  targetType: CustomFieldTargetType;
  isSelfUpdating: boolean;
  sessionId: string;
  /*
   * Field to allow setting of broadcasting on field update
   * Needed to prevent item updates in the scope,
   * when this should be implemented ONLY on Form - Save.
   * Otherwise, in case when user makes some changes and then hits the Cancel button
   * it was causing "false" updаte in other related views.
   * Example: comments in https://gtmhub.atlassian.net/browse/GVS-862
   */
  skipBroadcastingUpdates: boolean;
  targetCustomFieldsUI: CfMapUI;
  requiredTargetCfMap: CfMapRequired;
  customFields: ICustomField[];
  editDatepickerMode: { [fieldName: string]: boolean };
  colorsMap: { [color: string]: number };
  disabled: boolean;
  workflowEditableCustomFields: string[];
  disableRequired: boolean;
  filterEmptyValues: boolean;
  onUpdate: (params: IOnUpdateParams) => void;
  checkRequired: (params: { requiredCf: { areFilled: boolean } }) => void;

  static $inject = ["$scope", "UpdateService", "CustomFieldActions", "$ngRedux", "UIErrorHandlingService", "$rootScope"];

  constructor(
    private $scope: IScope,
    private updateService: UpdateService,
    private customFieldActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $rootScope: ITraceRootScopeService
  ) {
    this.itemEvents = new ItemEvents(this.$scope);

    this.unsubscribeFromCustomFields = this.$scope.$watch("$ctrl.customFields", (newValue: ICustomField[]) => {
      if (newValue?.length) {
        this.targetCustomFieldsUI = this.patchTargetCustomFields();
        this.requiredTargetCfMap = this.getRequiredTargetCfMap();
        this.checkIfAllRequiredNotEmpty();
      } else {
        this.checkRequired({ requiredCf: { areFilled: true } });
      }
    });
  }

  $onInit(): void {
    this.colorsMap = this.generateColorsMap(colors.customFieldColors);
    this.unsubscribeFromStore = this.$ngRedux.connect(this.bindStateToScope.bind(this))(this);
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
    this.targetCustomFieldsUI = this.patchTargetCustomFields();
    this.requiredTargetCfMap = this.getRequiredTargetCfMap();
    this.checkIfAllRequiredNotEmpty();
  }

  $onDestroy(): void {
    this.unsubscribeFromStore();
    this.unsubscribeFromCustomFields();
  }

  $onChanges(onChangesObj: IOnChangesObject): void {
    if (
      onChangesObj &&
      onChangesObj.target &&
      Object.keys(onChangesObj.target.previousValue).length > 0 &&
      !angular.equals(onChangesObj.target.currentValue, onChangesObj.target.previousValue)
    ) {
      // Make sure to update targetCustomFieldsUI in case of changes of this target from this parent scope.
      this.targetCustomFieldsUI = this.patchTargetCustomFields();
    }

    // If the session has been changed load the new custom fields and remove the current custom fields.
    if (onChangesObj && onChangesObj.sessionId && onChangesObj.sessionId.currentValue !== onChangesObj.sessionId.previousValue) {
      this.unsubscribeFromStore = this.$ngRedux.connect(this.bindStateToScope.bind(this))(this);
    }
  }

  isFieldPartOfWorkflow(filedName: string): boolean {
    return this.workflowEditableCustomFields?.includes(filedName);
  }

  getPredefinedValues(query: string, predefinedValues: CustomFieldPredefinedValue[]): string[] {
    const stringValues = predefinedValues.map((v) => (typeof v === "object" ? v.value : v.toString()));
    return filterPredefinedValues(query, stringValues);
  }

  private bindStateToScope(state: ICustomFieldsStoreState) {
    let customFieldsFilteredBySession = [];
    //filter custom fields per session, also keep  global ones
    if (state.customFields.map[this.targetType]) {
      customFieldsFilteredBySession = state.customFields.map[this.targetType].filter((value) => {
        return !value.operationalType || value.operationalType == "global" || (value.targetIds && value.targetIds.includes(this.sessionId));
      });
    }
    return {
      customFields: customFieldsFilteredBySession,
    };
  }

  // does not support metric checkin update because for now we only patch the checkin note
  updateCustomField(fieldName: string, cf: CfDropdownUI | CfMultiSelectUI | string, params?: { action: "added" | "removed" }): void {
    this.$rootScope.traceAction("update_custom_field", () => {
      if (params) {
        this.updateCfSelectFromListMapValues(fieldName, cf as string, params);
      } else {
        this.updateCfMapValues(fieldName, cf);
      }

      this.checkIfAllRequiredNotEmpty();
      // this means that the component won't handle the patch of the target, instead the parent controller will handle it iself
      // the component will only emit the new value to the parent
      if (this.isSelfUpdating) {
        this.onUpdate({ customFields: this.target.customFields });

        if (!this.skipBroadcastingUpdates) {
          const updatedTarget = this.target.customFields[fieldName];
          const update: Partial<SupportedUpdateItem> = { customFields: this.target.customFields };
          this.itemEvents.broadcastItemUpdated({ item: updatedTarget, type: this.targetType, update });
        }
        return;
      }

      const update: Partial<SupportedUpdateItem> = { customFields: this.target.customFields };
      this.updateService.update(this.targetType, { id: this.target.id, ...update }).then(
        (updatedTarget) => {
          if (!this.skipBroadcastingUpdates) {
            this.itemEvents.broadcastItemUpdated({ item: updatedTarget, type: this.targetType, update });
          }
        },
        (err) => this.uiErrorHandlingService.handleModal(err)
      );
    });
  }

  private updateCfSelectFromListMapValues = (fieldName: string, cf: string, params: { action: "added" | "removed" }): void => {
    if (params.action === "added") {
      if (this.targetCustomFieldsUI[fieldName]) {
        (this.targetCustomFieldsUI[fieldName] as CfSelectFromListUI).push(cf);
        (this.target.customFields[fieldName] as CfSelectFromListUI).push(cf);
      } else {
        this.targetCustomFieldsUI[fieldName] = [cf];
        this.target.customFields[fieldName] = [cf];
      }
    } else {
      this.targetCustomFieldsUI[fieldName] = (this.targetCustomFieldsUI[fieldName] as CfSelectFromListUI).filter((assigneeId) => assigneeId !== cf);
      this.target.customFields[fieldName] = (this.target.customFields[fieldName] as CfSelectFromListUI).filter((assigneeId) => assigneeId !== cf);
    }

    // Custom field is required
    if (Object.keys(this.requiredTargetCfMap).includes(fieldName)) {
      this.requiredTargetCfMap[fieldName] = {
        value: (this.targetCustomFieldsUI[fieldName] as CfSelectFromListUI).length ? this.targetCustomFieldsUI[fieldName] : null,
        empty: (this.targetCustomFieldsUI[fieldName] as CfSelectFromListUI).length ? false : true,
      };
    }
  };

  /**
   * Helper function to take the real value from a customField.
   * This is required because the library for the input adds elements to array as objects,
   * when customField is of type: {@link cfDropdownUI} or {@link cfMultiSelectUI}.
   * So we need only names when its {@link cfDropdownUI} or {@link cfMultiSelectUI},
   * or the "real" value of the customField (itself) when it is different from one of the above types.
   *
   * @param customField The CustomFields which value is to be retrieved
   * @returns The real value of the customField, i.e. string or flattened array with options names in case of {@link cfMultiSelectUI}.
   */
  private getCfValue = (customField: CfDropdownUI | CfMultiSelectUI | CfSelectFromListUI | string) => {
    let cfValue = null;

    if (customField) {
      if ((customField as CfDropdownUI).value) {
        // Read value from a custom select drop down
        cfValue = (customField as CfDropdownUI).value;
      } else if ((customField as CfMultiSelectUI)[0] && (customField as CfMultiSelectUI)[0].value) {
        /*
         * Multi-select value
         * here the value is array of objects in form:
         * [{value: "OPTION NAME1"}, {value: "OPTION NAME2"}...}]
         *
         * Flatten value entries into array of option values, i.e. ["OPTION NAME1", "OPTION NAME2"...]
         */
        cfValue = (customField as CfMultiSelectUI).map((multiSelectOption) => multiSelectOption.value);
      } else {
        /*
         * customField is a:
         *    1. simple string
         *    2.  regular array with values
         *        or of type: cfSelectFromListUI, which is also array of strings
         *
         * Just read it as it is.
         */
        cfValue = customField;
      }
    }

    return cfValue;
  };

  private isCfEmpty = (customFieldValue: Array<string> | string) => {
    let isEmpty = true;

    if (customFieldValue) {
      if (Array.isArray(customFieldValue)) {
        isEmpty = customFieldValue.length == 0;
      } else {
        isEmpty = (<string>customFieldValue).trim().length == 0;
      }
    }

    return isEmpty;
  };

  private updateCfMapValues = (fieldName: string, cf: CfDropdownUI | CfMultiSelectUI | string) => {
    if (!this.target.customFields) {
      this.target.customFields = {};
    }

    const cfValue = this.getCfValue(cf);
    this.target.customFields[fieldName] = cfValue;
    this.targetCustomFieldsUI[fieldName] = cf;

    if (Object.keys(this.requiredTargetCfMap).includes(fieldName)) {
      // Custom field is required => update it in related collection
      this.requiredTargetCfMap[fieldName] = {
        value: cfValue,
        empty: this.isCfEmpty(cfValue),
      };
    }
  };

  private generateColorsMap = (colors: string[]): { [color: string]: number } => {
    return colors.reduce((map, color, index) => {
      if (!map[color]) {
        map[color] = index + 1;
      }
      return map;
    }, {});
  };

  private patchTargetCustomFields = (): CfMapUI => {
    if (this.target.customFields) {
      return this.customFields.reduce((acc, cf) => {
        if (this.target.customFields[cf.name]) {
          acc[cf.name] = copy(this.target.customFields[cf.name]);
          if (cf.visualizationKind === "gtmhub.select") {
            const predefinedValues = cf.settings.predefinedValues as DropdownCustomFieldPredefinedValue[];
            acc[cf.name] = predefinedValues.find((option) => option.value === this.target.customFields[cf.name]);
          }
        } else if (cf.settings && cf.settings.validations && cf.settings.validations.required) {
          /*
           * Current custom field is required in this controller (form) and is missing in the target.
           *
           * DEFAULT required custom field on target with default value.
           *
           * In this case, in order to provide proper validation of the curent customFields,
           * we need to make sure that this.target.customFields has default values for each required item in this directive customFields.
           *
           * Otherwise validation might not go as expected.
           *
           * Especially on required fields of type: multiselect.
           */
          if (cf.type.indexOf("[]") > -1) {
            // Multiselect customFields' values are handled as array of selected options. Default them to empty array.
            this.target.customFields[cf.name] = [];
            acc[cf.name] = [];
          } else {
            // Other customFields' values are stored as strings. Default them to empty string.
            this.target.customFields[cf.name] = "";
            acc[cf.name] = "";
          }
        } else if (this.target.customFields[cf.name] === undefined && cf.type.indexOf("[]") > -1 && cf.visualizationKind === "gtmhub.multiselect") {
          //initialise gtmhub.multiselect with empty array, otherwise the 1st time you select is getting a value of undefined and the change is not saved
          acc[cf.name] = [];
        }

        return acc;
      }, {});
    } else {
      this.target["customFields"] = {};
      return {};
    }
  };

  private getRequiredTargetCfMap = (): CfMapRequired => {
    return this.customFields.reduce((requiredCfs, cf) => {
      if (cf.settings.validations.required) {
        const requiredCustomField = this.targetCustomFieldsUI[cf.name];
        const cfValue = this.getCfValue(requiredCustomField);

        requiredCfs[cf.name] = {
          value: cfValue,
          empty: this.isCfEmpty(cfValue),
        };
      }

      return requiredCfs;
    }, {});
  };

  private checkIfAllRequiredNotEmpty = () => {
    const requiredCf = Object.values(this.requiredTargetCfMap);
    this.checkRequired({ requiredCf: { areFilled: !requiredCf.length || !requiredCf.some((cf) => cf.empty) } });
  };
}

export const EditableCustomFieldsComponent: IComponentOptions = {
  // change the way of target binding and
  // remove the should not update variable
  // once all views do not need two way binding and use the components update method
  bindings: {
    target: "<",
    targetType: "@",
    isSelfUpdating: "<",
    skipBroadcastingUpdates: "<",
    onUpdate: "&",
    disabled: "<",
    checkRequired: "&",
    disableRequired: "<",
    sessionId: "<",
    filterEmptyValues: "<",
    workflowEditableCustomFields: "<",
  },
  controller: EditableCustomFieldsCtrl,
  templateUrl: require("./editable-custom-fields.jade"),
};
